import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { LocationStatusItem } from '../model/location-status-item';
import { ErrorHandlerService } from './error-handler.service';
import { AdapterStatusItem } from '../model/adapter-status-item';
import { EmployerDto, EventV2Dto, UserGrantDto } from '../api/exa';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import * as autobahn from 'autobahn-browser';
import { EmployerService } from './employer.service';
import { ExaGrant } from '../model/exa-grant';
import { environment } from 'src/environments/environment';
import { HelperFunctions } from '../helper/helper-functions';

@Injectable({
  providedIn: 'root',
})
export class PubSubService {
  className = 'PubSubService';
  readonly WS_URI = 'wss://pubsub.exatrek.de:8042/ws';
  // appClient
  readonly TOPIC_STATUS_UPDATE = 'de.exa.trek.statusupdate.'; //+EMPLOYERNAME
  readonly TOPIC_EVENTS = 'de.exa.trek.events.'; //+EMPLOYERNAME
  readonly TOPIC_ADAPTER_STATUS = 'de.exa.trek.adapterstatus.'; //+EMPLOYERNAME
  session = null;
  connection = null;
  actualCustomerUserName = null;
  employer: EmployerDto;
  private events$ = new Subject<[string, EventV2Dto]>();
  private statusUpdate$ = new Subject<[string, LocationStatusItem]>();
  private adapterStatus$ = new Subject<[string, AdapterStatusItem]>();

  private continueSubscription = new Subject<boolean>();

  adminSession = null;
  adminConnection = null;
  private adminStatusSub: any;
  private adminStatusUpdate$ = new Subject<LocationStatusItem>();

  private isSysAdmin = false;
  private isAdmin = false;
  private isDisponent = false;
  private isBerater = false;
  private isFahrer = false;

  constructor(
    private auth: AuthService,
    private employerService: EmployerService,
    private errHandler: ErrorHandlerService,
    private log: NGXLogger
  ) {
    this.isSysAdmin = this.auth.hasGrant(ExaGrant.ADMIN);
    void this.openConnection().then(() => {
      this.employerService.onSelectEmployer().subscribe({
        next: (employer) => {
          if (employer) {
            const isAdmin = this.isSysAdmin || this.auth.hasGrantOwner(ExaGrant.ENTERPRISE_ADMIN, employer.id);
            const isDisponent = this.auth.hasGrantOwner(ExaGrant.ENTERPRISE_DISPONENT, employer.id) ||
              this.auth.hasGrantOwner(ExaGrant.ENTERPRISE_CONSULTANT, employer.id);
            this.initEmployerTopics(employer.username!, isAdmin, isDisponent);
          }
        },
        error: (err) => {}
      });
    });
  }

   private openConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.session) {
        resolve();
      } else {
        if (!this.connection) {
          this.log.debug(this.className + ' Try to create connection...');
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const me = this;

          const prefix = 'exaApp_';
          let uniqueAuthId = prefix + new Date().getTime() + '_' + Math.floor(Math.random() * 500);
          if (!environment.production) {
              uniqueAuthId += '_-$-qs-$-';
          }

          this.connection = new autobahn.Connection({
            url: this.WS_URI,
            realm: 'realm1',
            authmethods: ['ticket'],
            authid: uniqueAuthId,
            onchallenge: (_session: any, method: string, _extra: any) => {
              if (method === 'ticket') {
                // return current token
                return me.auth.getAuthenticatedUserToken().token;
              }
              return '';
            },
          });
          // this callback is fired during WAMP-CRA authentication

          this.connection.onopen = function (session: any) {
            me.log.debug(me.className + ': connection is opened: ', session);
            me.session = session;
            resolve();
          };
          this.connection.open();
          this.log.debug(this.className + ' connection created');
        }
        // else connection already available
      }
    });
  }

    private initEmployerTopics(employerUsername: string, isAdmin: boolean, isDisponent: boolean) {
    // options = SubscribeOptions(match = u'exact', details_arg = 'details')
    if (employerUsername) {
      let topicName;
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const me = this;
      if (isAdmin || isDisponent) {
        topicName = this.TOPIC_STATUS_UPDATE + employerUsername;
        this.log.debug(this.className + '- Try subscribed to topic ', topicName);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.session.subscribe(topicName, onStatusUpdate, { match: 'prefix', details_arg: 'details' }).then(
          function (sub: any) {
            me.log.debug(me.className + '- subscribed to topic ', sub);
          },
          function (err: any) {
            me.log.debug(me.className + '- failed to subscribed: ', err);
          }
        );

        topicName = this.TOPIC_EVENTS + employerUsername;
        this.log.debug(this.className + '- Try subscribed to topic ', topicName);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.session.subscribe(topicName, onEventUpdate, { match: 'prefix', details_arg: 'details' }).then(
          function (sub: any) {
            me.log.debug(me.className + '- subscribed to topic ', sub);
          },
          function (err: string) {
            me.log.debug(me.className + '- failed to subscribed: ' + err);
          }
        );
      }

      if (isAdmin) {
        topicName = this.TOPIC_ADAPTER_STATUS + employerUsername;
        this.log.debug(this.className + '- Try subscribed to topic ', topicName);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.session.subscribe(topicName, onAdapterStatus, { match: 'prefix', details_arg: 'details' }).then(
          function (sub: any) {
            me.log.debug(me.className + '- subscribed to topic ', sub);
          },
          function (err: any) {
            me.log.debug(me.className + '- failed to subscribed: ', err);
          }
        );
      }

      // eslint-disable-next-line no-inner-declarations
      function onStatusUpdate(args: string[], t: any, details: any) {
        const topic = details['topic'];
        const employer: string = topic.toString().replace(me.TOPIC_STATUS_UPDATE, '');
        me.log.debug(me.className + '- MachineStatusUpdate: ', [employer, args]);
        me.statusUpdate$.next([employer, JSON.parse(HelperFunctions.replaceAll(args[0], ' ', '')) as LocationStatusItem]);
      }

      // eslint-disable-next-line no-inner-declarations
      function onEventUpdate(args: string[], t: string, details: any) {
        const topic = details['topic'];
        const employer: string = topic.toString().replace(me.TOPIC_EVENTS, '');
        me.log.debug(me.className + '- Event: ', [employer, args]);
        me.events$.next([employer, JSON.parse(args[0]) as EventV2Dto]);
        // me.messageService.addEventToMessages(JSON.parse(args[0]) as EventV2Dto);
      }

      // eslint-disable-next-line no-inner-declarations
      function onAdapterStatus(args: string[], t: string, details: any) {
        const topic = details['topic'];
        const employer: string = topic.toString().replace(me.TOPIC_ADAPTER_STATUS, '');
        me.log.debug(me.className + '- AdapterStatusUpdate: ', [employer, args]);
        me.adapterStatus$.next([employer, JSON.parse(HelperFunctions.replaceAll(args[0], ' ', '')) as AdapterStatusItem]);
      }
    }
  }

  private closeConnection() {
    if (this.connection) {
      this.connection.close();
      this.log.debug('Close websocket');
    }
    this.connection = null;
    this.session = null;
  }

  private openAdminConnection() {
    if (!this.adminConnection) {
      // const autobahn = require('autobahn');
      this.log.debug('Try to create ADMIN connection...');
      var me = this;

      const connectionOptions: autobahn.IConnectionOptions = {
        url: this.WS_URI,
        realm: 'realm1',
        authmethods: ['ticket'],
        authid: this.auth.getAuthenticatedUser().username,
        onchallenge: function onChallenge(session, method, extra) {
          if (method === 'ticket') {
            // return current token
            return me.auth.getAuthenticatedUserToken().token;
          }
        },
        //retry_if_unreachable: true,
        //max_retries?: number | undefined;
        //initial_retry_delay?: number | undefined;
        //max_retry_delay?: number | undefined;
        //retry_delay_growth?: number | undefined;
        //retry_delay_jitter?: number | undefined;
      };
      this.adminConnection = new autobahn.Connection(connectionOptions);

      this.adminConnection.onopen = function (session) {
        me.log.debug('ADMIN connection is opened: ', session);
        me.adminSession = session;
        // 1) subscribe to a topic
        function onStatusUpdate(args, topic, details) {
          me.log.debug('received ADMIN StatusUpdate (args, topic, details): ', args, topic, details);
          // this.log.debug("received StatusUpdate: " + args[0][2]);
          me.adminStatusUpdate$.next(JSON.parse(args[0]));
        }

        var topicName;
        topicName = me.TOPIC_STATUS_UPDATE;
        me.log.debug('Try subscribed to topic ', topicName);
        session.subscribe(topicName, onStatusUpdate, { match: 'prefix', details_arg: 'details' }).then(
          function (sub) {
            me.adminStatusSub = sub;
            me.log.debug('subscribed to topic ', sub);
          },
          function (err) {
            me.log.debug('failed to subscribed: ', err);
          }
        );
      };
      this.adminConnection.open();
      this.log.debug('ADMIN connection created');
    } else {
      // Already open
    }
  }

  public closeAdminConnection() {
    if (this.adminConnection) {
      this.adminStatusSub.unsubscribe();
      this.adminConnection.close();
      this.log.debug('Close ADMIN websocket');
    }
    this.adminConnection = null;
    this.adminSession = null;
  }

  public onEvents(): Observable<[string, EventV2Dto]> {
    return this.events$;
  }

  public publishEvent(event: EventV2Dto): Observable<any> {
    return this.publishToTopic(this.TOPIC_EVENTS, JSON.stringify(event));
  }

  public publishStatusUpdate(status: LocationStatusItem): Observable<any> {
    return this.publishToTopic(this.TOPIC_STATUS_UPDATE, JSON.stringify(status));
  }

  private publishToTopic(topic, pubData): Observable<any> {
    let ret = new Subject<any>();
    if (this.session) {
      const user = this.auth.getAuthenticatedUser();
      var data = [[new Date().toJSON(), user ? user.username : '', pubData]];
      const me = this;
      this.session.publish(topic + this.employer.username, data, {}, { acknowledge: true, exclude_me: false }).then(
        function (publication) {
          this.log.debug('published', publication);
          ret.next(publication);
        },
        function (error) {
          this.log.debug('publication error', error);
          ret.error(error);
        }
      );
    } else {
      ret.error('Keine Verbindung!');
    }
    return ret;
  }

  public onStatusUpdate(): Observable<[string, LocationStatusItem]> {
    return this.statusUpdate$;
  }

  public onAdapterStatusUpdate(): Observable<[string, AdapterStatusItem]> {
    return this.adapterStatus$;
  }

  public onAdminStatusUpdate(): Observable<LocationStatusItem> {
    return this.adminStatusUpdate$;
  }

  public getContinueSubscription(): Observable<boolean> {
    return this.continueSubscription.asObservable();
  }
  public setContinueSubscription(newValue: boolean): void {
    this.continueSubscription.next(newValue);
  }

  public initAdminConnection() {
    if (this.isSysAdmin) {
      this.closeAdminConnection();
      this.openAdminConnection();
    } else {
      this.closeAdminConnection();
    }
  }
}
